import { useState, useCallback, useEffect } from 'react'
import { useVoiceAgent } from './hooks/useVoiceAgent'
import { VoiceButton } from './components/VoiceButton'
import { AudioVisualizer } from './components/AudioVisualizer'
import { AdminApp } from './admin/AdminApp'
import type { Voice } from './admin/types'

const copy = {
  ru: {
    status: {
      disconnected: 'Отключено',
      connecting: 'Подключение',
      connected: 'Подключено',
      error: 'Ошибка',
    },
    actions: {
      connect: 'Подключиться',
      disconnect: 'Отключиться',
      status: 'Статус подключения',
    },
    hints: {
      disconnected: 'Нажмите микрофон, чтобы начать звонок',
      connecting: 'Подключение...',
      error: 'Проверьте подключение и настройки',
      recording: 'Слушаю...',
      speaking: 'Агент отвечает... можно перебить голосом',
      ready: 'Звонок активен',
    },
    footer: 'Трамплинный парк в Падерборне',
    mic: {
      start: 'Начать звонок',
      stop: 'Завершить звонок',
      interrupt: 'Агент говорит',
    },
  },
  de: {
    status: {
      disconnected: 'Getrennt',
      connecting: 'Verbinden',
      connected: 'Verbunden',
      error: 'Fehler',
    },
    actions: {
      connect: 'Verbinden',
      disconnect: 'Trennen',
      status: 'Verbindungsstatus',
    },
    hints: {
      disconnected: 'Mikrofon tippen, um den Anruf zu starten',
      connecting: 'Verbindung...',
      error: 'Bitte Verbindung und Einstellungen pruefen',
      recording: 'Ich hoere zu...',
      speaking: 'Der Assistent antwortet... per Stimme unterbrechen',
      ready: 'Anruf aktiv',
    },
    footer: 'Trampolinpark in Paderborn',
    mic: {
      start: 'Anruf starten',
      stop: 'Anruf beenden',
      interrupt: 'Assistent spricht',
    },
  },
} as const

export default function App() {
  if (window.location.pathname.startsWith('/admin')) {
    return <AdminApp />
  }

  return <VoiceApp />
}

function renderVoiceLabel(voices: Voice[], explicitVoice: string | null, defaultVoice: string) {
  const activeId = explicitVoice ?? defaultVoice
  if (!activeId) return null
  const match = voices.find(voice => voice.id === activeId)
  const label = match?.label || activeId
  const isExplicit = explicitVoice !== null && explicitVoice !== undefined
  return (
    <>
      <span className="logo-voice-label">Voice</span>
      <strong>{label}</strong>
      {isExplicit ? <em>test</em> : <em>default</em>}
    </>
  )
}

function VoiceApp() {
  const [lang, setLang] = useState<'ru' | 'de'>('de')
  const [voiceFromUrl, setVoiceFromUrl] = useState<string | null>(null)
  const [voices, setVoices] = useState<Voice[]>([])
  const [defaultVoice, setDefaultVoice] = useState('')
  const labels = copy[lang]

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    setVoiceFromUrl(params.get('voice'))
  }, [])

  useEffect(() => {
    fetch('/api/voice/config', { credentials: 'same-origin' })
      .then(response => (response.ok ? response.json() : null))
      .then(data => {
        if (!data) return
        if (Array.isArray(data.voices)) setVoices(data.voices)
        if (typeof data.default_voice === 'string') setDefaultVoice(data.default_voice)
      })
      .catch(() => undefined)
  }, [])

  const {
    status,
    isRecording,
    isSpeaking,
    transcript,
    error,
    startRecording,
    stopRecording,
    disconnect,
  } = useVoiceAgent({ lang, voice: voiceFromUrl ?? undefined })

  const handleVoiceToggle = useCallback(() => {
    if (isRecording) {
      stopRecording()
    } else {
      startRecording()
    }
  }, [isRecording, startRecording, stopRecording])

  const handleLangChange = useCallback((nextLang: 'ru' | 'de') => {
    if (nextLang === lang) return

    disconnect()
    setLang(nextLang)
  }, [disconnect, lang])

  const handleStatusClick = useCallback(() => {
    if (status === 'connected') {
      disconnect()
    }
  }, [disconnect, status])

  const getHint = () => {
    if (status === 'disconnected') return labels.hints.disconnected
    if (status === 'connecting') return labels.hints.connecting
    if (status === 'error') return labels.hints.error
    if (isSpeaking) return labels.hints.speaking
    if (isRecording) return labels.hints.recording
    return labels.hints.ready
  }

  const statusActionLabel = status === 'connected' ? labels.actions.disconnect : labels.actions.connect

  return (
    <div className="app">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="logo">
            <span className="logo-icon">🏃</span>
            <div className="logo-text">
              <h1>Activity Park</h1>
              <span className="logo-subtitle">Paderborn</span>
              <span className="logo-voice">{renderVoiceLabel(voices, voiceFromUrl, defaultVoice)}</span>
            </div>
          </div>

          <div className="header-actions">
            <div className="lang-switcher">
              <button
                className={`lang-btn ${lang === 'de' ? 'active' : ''}`}
                onClick={() => handleLangChange('de')}
                aria-label="Deutsch"
              >
                🇩🇪
              </button>
              <button
                className={`lang-btn ${lang === 'ru' ? 'active' : ''}`}
                onClick={() => handleLangChange('ru')}
                aria-label="Русский"
              >
                🇷🇺
              </button>
            </div>

            <button
              className={`status-lamp status-lamp-${status}`}
              onClick={handleStatusClick}
              disabled={status !== 'connected'}
              aria-label={`${labels.actions.status}: ${labels.status[status]}. ${statusActionLabel}`}
              title={error || `${labels.actions.status}: ${labels.status[status]}`}
            >
              <span className="status-lamp-dot" />
            </button>
          </div>
        </div>
      </header>

      {error && (
        <div className="status-error-banner">
          {error}
        </div>
      )}

      <main className="main">
        <div className="voice-area">
          <AudioVisualizer isActive={isRecording || isSpeaking} isRecording={isRecording} />

          {transcript && (
            <div className="transcript-preview">
              <p>{transcript}</p>
            </div>
          )}

          <VoiceButton
            isRecording={isRecording}
            isSpeaking={isSpeaking}
            disabled={status === 'connecting'}
            onToggle={handleVoiceToggle}
            labels={labels.mic}
          />

          <p className="hint">{getHint()}</p>
        </div>
      </main>

      <footer className="footer">
        <p>{labels.footer}</p>
        <p className="footer-contacts">
          📞 +49 (0) 5251 777 3690 &nbsp;|&nbsp; 📍 Pamplona Straße 15
        </p>
      </footer>
    </div>
  )
}
